import type {
  ChecklistFieldMode,
  ChecklistItemKind,
  IChecklistItem,
  ITaskAttachment,
} from '../interfaces/IScheduler.js';
import { throwForError } from './scheduleErrors.js';
import type { AuthedFetch } from './transport.js';

// The task checklist's own API (docs/specs/2026-09-28-task-checklist-design.md):
// the templates a site may apply, a committed task's amendment, and evidence
// photo upload (a check item's, or a task comment's — one store, one serving
// route). Ticking stays with the granular execution writes
// (`setTaskCheck`, schedule.ts).

/** One item of a checklist template — a definition without a task identity. */
export interface IChecklistTemplateItem {
  id: string;
  kind: ChecklistItemKind;
  content: string;
  photo: ChecklistFieldMode;
  comment: ChecklistFieldMode;
  blackboard: ChecklistFieldMode;
}

export interface IChecklistTemplate {
  id: string;
  name: string;
  items: IChecklistTemplateItem[];
}

/** Done was refused: the checklist still has open checks (409). Carries
 *  their ids, so a surface can point at them. */
export class ChecklistIncompleteError extends Error {
  constructor(public readonly openItemIds: string[]) {
    super(`${openItemIds.length} checklist item(s) still open`);
    this.name = 'ChecklistIncompleteError';
  }
}

/**
 * The server refused a tick for good — an item a concurrent amendment removed
 * (404), a description (400), missing evidence (422), a task no longer
 * committed (409). Retrying sends the same answer; re-read the plan instead.
 */
export class ChecklistTickRefusedError extends Error {
  constructor(public readonly status: number, public readonly code: string | undefined) {
    super(`checklist tick refused: ${status} ${code ?? ''}`.trim());
    this.name = 'ChecklistTickRefusedError';
  }
}

const TICK_REFUSALS = new Set([400, 404, 409, 422]);

/** The `ChecklistTickRefusedError` a refused tick carries, if any. */
export async function tickRefusal(res: Response): Promise<ChecklistTickRefusedError | null> {
  if (!TICK_REFUSALS.has(res.status)) return null;
  const body = await res.clone().json().catch(() => null);
  return new ChecklistTickRefusedError(res.status, body?.error?.code);
}

/** The `ChecklistIncompleteError` a refused status write carries, if any. */
export async function checklistRefusal(res: Response): Promise<ChecklistIncompleteError | null> {
  if (res.status !== 409) return null;
  const body = await res.clone().json().catch(() => null);
  if (body?.error?.code !== 'CHECKLIST_INCOMPLETE') return null;
  return new ChecklistIncompleteError(body.error.details?.openItemIds ?? []);
}

const taskPath = (siteId: string, scheduleNumber: string, taskId: string): string =>
  `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/${encodeURIComponent(taskId)}`;

export function createChecklistApi(authedFetch: AuthedFetch) {
  /** The templates a site may apply (Genba's, copied onto a task when applied). */
  async function fetchChecklistTemplates(siteId: string, signal?: AbortSignal): Promise<IChecklistTemplate[]> {
    const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/checklist-templates`, { signal });
    if (res.ok) return ((await res.json()) as { templates: IChecklistTemplate[] }).templates;
    return throwForError(res, 'list checklist templates');
  }

  /** Replace a COMMITTED task's checklist; resolves with the stored items and
   *  the schedule version the amendment produced. */
  async function amendTaskChecklist(
    siteId: string,
    scheduleNumber: string,
    taskId: string,
    items: IChecklistItem[],
  ): Promise<{ checklist: IChecklistItem[]; version: string }> {
    // Definitions only: a completion is execution, never part of an amendment.
    const definitions = items.map(({ completion, ...definition }) => {
      void completion;
      return definition;
    });
    const res = await authedFetch(`${taskPath(siteId, scheduleNumber, taskId)}/checklist`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: definitions }),
    });
    if (res.ok) {
      const body = (await res.json()) as { checklist: IChecklistItem[]; version: string };
      return { checklist: body.checklist, version: body.version };
    }
    return throwForError(res, 'amend task checklist');
  }

  /** POST one image as multipart; resolves with the stored attachment. */
  async function postPhoto(url: string, file: Blob, fileName: string, what: string): Promise<ITaskAttachment> {
    const form = new FormData();
    form.append('file', file, fileName);
    const res = await authedFetch(url, { method: 'POST', body: form });
    if (res.ok) return (await res.json()) as ITaskAttachment;
    return throwForError(res, what);
  }

  /** Store one evidence photo for a check item; tick with the attachment it returns. */
  function uploadChecklistPhoto(
    siteId: string,
    scheduleNumber: string,
    taskId: string,
    itemId: string,
    file: Blob,
    fileName: string,
  ): Promise<ITaskAttachment> {
    const url = `${taskPath(siteId, scheduleNumber, taskId)}/checks/${encodeURIComponent(itemId)}/photo`;
    return postPhoto(url, file, fileName, 'upload checklist photo');
  }

  /** Store one photo for a task comment; post the comment with the attachment
   *  it returns (served from the same evidence route as a checklist photo). */
  function uploadCommentPhoto(
    siteId: string,
    scheduleNumber: string,
    taskId: string,
    file: Blob,
    fileName: string,
  ): Promise<ITaskAttachment> {
    return postPhoto(`${taskPath(siteId, scheduleNumber, taskId)}/comment-photos`, file, fileName, 'upload comment photo');
  }

  /** A stored evidence photo's bytes — its `url` is an API path, fetched with
   *  the session like any other call (an <img> cannot carry a Bearer). */
  async function fetchChecklistPhoto(url: string, signal?: AbortSignal): Promise<Blob> {
    const res = await authedFetch(url, { signal });
    if (res.ok) return res.blob();
    return throwForError(res, 'fetch checklist photo');
  }

  return { fetchChecklistTemplates, amendTaskChecklist, uploadChecklistPhoto, uploadCommentPhoto, fetchChecklistPhoto };
}
